// treemap.

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import {
  applySelectionStyle,
  baseChartOption,
  bucketKey,
  firstFieldRef,
  isEmpty,
} from "./_echartsHelpers";

export function TreemapVisual({ spec, data, filters, onFilter }: VisualProps) {
  const empty = isEmpty(data);
  const rows = data.rows ?? [];
  const fields = spec.fields ?? {};
  const categoryKey = bucketKey(fields.Group) ?? bucketKey(fields.Category);
  const valueKey = bucketKey(fields.Values);

  const seriesData =
    categoryKey && valueKey
      ? rows.map((r) => ({
          name: String(r[categoryKey] ?? ""),
          value: Number(r[valueKey] ?? 0),
        }))
      : [];

  const categoryField = firstFieldRef(fields.Group) ?? firstFieldRef(fields.Category);

  // AGENT-C: treemap has no cartesian grid or axes — null them out and keep
  // the PBI tooltip chrome with an item-level trigger.
  const base = baseChartOption();
  const baseOption: EChartsOption = {
    ...base,
    xAxis: undefined,
    yAxis: undefined,
    grid: undefined,
    tooltip: { ...(base.tooltip as object), trigger: "item" },
    series: [
      {
        type: "treemap",
        roam: false,
        breadcrumb: { show: false },
        label: { show: true, formatter: "{b}" },
        data: seriesData,
      },
    ],
  };

  // P1-selection: dim non-selected leaves when a matching filter is active.
  // Treemap leaves carry their category on the .name field.
  const categoryKeyFq = categoryField?.table && categoryField?.column
    ? `${categoryField.table}.${categoryField.column}`
    : categoryKey;
  const option = applySelectionStyle(
    baseOption,
    filters ?? {},
    categoryKeyFq,
    seriesData.map((d) => d.name),
  );

  const onClick = (params: { name?: string }) => {
    if (!categoryField?.table || !categoryField?.column || !params.name) return;
    onFilter(`${categoryField.table}.${categoryField.column}`, {
      op: "eq",
      value: params.name,
      source: spec.id,
    });
  };

  return (
    <ChartShell title={spec.title} isEmpty={empty} status={data.status}>
      <ReactECharts
        option={option}
        notMerge
        style={{ height: "100%", width: "100%" }}
        onEvents={{ click: onClick }}
      />
    </ChartShell>
  );
}
